import { CONCIERGE_TYPES } from '@/store/constant'
import Loader from '@components/Loader'
import { useAuth } from '@hooks/useAuth'
import { useConciergeList } from '@hooks/useConciergeList'
import { Wisdom, WisdomContext } from '@hooks/useWisdom'
import { useWisdomId } from '@hooks/useWisdomId'
import {
  deleteField,
  doc,
  getFirestore,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore'
import { ReactNode, useEffect, useState } from 'react'

interface WisdomProviderProps {
  children: ReactNode
}

const WISDOMS_COLLECTION = 'wisdoms'
export function WisdomProvider({ children }: WisdomProviderProps) {
  const [wisdom, setWisdom] = useState<Wisdom | null>(null)
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()
  const { setConciergeType } = useConciergeList()
  const wisdomId = useWisdomId()
  const db = getFirestore()

  useEffect(() => {
    let unsubscribe: () => void

    async function fetchWisdom() {
      const docRef = doc(db, WISDOMS_COLLECTION, wisdomId)
      unsubscribe = onSnapshot(docRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const { frontend, backend } = docSnapshot.data()
          const wisdom = {
            ...frontend,
            venueSpec: backend.venue_spec,
            isCalendarAuthed: !!backend.google_oauth_doc_ref,
            isInstagramAuthed: !!backend.meta_ig_credentials_ref,
          } as Wisdom
          setWisdom(wisdom)

          if (backend.venue_spec) {
            setConciergeType(CONCIERGE_TYPES.BOOKING_LOVE)
          } else {
            setConciergeType(CONCIERGE_TYPES.WEB_WISDOM)
          }
        } else {
          console.error('Empty wisdom snapshot?')
        }
        setLoading(false)
      })
    }

    fetchWisdom()

    return function cleanup() {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [user.email, wisdomId, db, setConciergeType])

  async function updateWisdom(updates: Partial<Wisdom>) {
    if (!wisdom) return

    const docRef = doc(db, WISDOMS_COLLECTION, wisdomId)
    await updateDoc(docRef, {
      frontend: {
        ...wisdom,
        ...updates,
      },
    })
  }

  async function googleDeauth() {
    const docRef = doc(db, WISDOMS_COLLECTION, wisdomId)

    try {
      await updateDoc(docRef, { 'backend.google_oauth_doc_ref': null })
    } catch (err) {
      console.error('googleDeauth failed', err)
      throw err
    }
  }

  async function instagramDeauth() {
    const docRef = doc(db, WISDOMS_COLLECTION, wisdomId)

    try {
      await updateDoc(docRef, {
        'backend.meta_ig_credentials_ref': deleteField(),
      })
    } catch (err) {
      console.error('instagramDeauth failed', err)
      throw err
    }
  }

  if (wisdom === null || loading) {
    return <Loader />
  }

  return (
    <WisdomContext.Provider
      value={{ wisdom, setWisdom, updateWisdom, googleDeauth, instagramDeauth }}
    >
      {children}
    </WisdomContext.Provider>
  )
}
